// ── V156: 오늘의 작업 · 회의가 같이 쓰는 입력 칸 ─────────────────────────────────────
//   · OwnerPicker 담당자 고르기 — 직원·예전에 쓴 이름을 칩으로 누르고, 없으면 직접 적기
//   · DuePicker 마감일 (날짜만) — 오늘 · 내일 · 3일 뒤 · 1주 뒤 칩 + 달력
//   · shareText 위챗으로 보내기 — 휴대폰은 공유창, 컴퓨터는 복사 (발주서와 같은 방식)
import React, { useRef, useState } from 'react';
import { addDaysKey, OWNER_MAX } from '../utils/workMeta';

export const ORANGE = '#FF6F0F';
export const GREEN = '#07C160';

export const chipStyle = (on, tone = 'orange') => {
  const c = {
    orange: ['#FF6F0F', '#FFF4EC', '#C2410C'],
    blue: ['#2563EB', '#EFF6FF', '#1D4ED8'],
    green: ['#07C160', '#F0FDF4', '#15803D'],
  }[tone] || ['#FF6F0F', '#FFF4EC', '#C2410C'];
  return {
    padding: '6px 11px', borderRadius: 999, fontSize: 13, fontWeight: 700, cursor: 'pointer', whiteSpace: 'nowrap',
    border: `1.5px solid ${on ? c[0] : '#E4E4E7'}`, background: on ? c[1] : '#fff', color: on ? c[2] : '#52525B',
  };
};

const inputStyle = { padding: '8px 10px', borderRadius: 9, border: '1.5px solid #E4E4E7', fontSize: 14, minWidth: 0, fontFamily: 'inherit', background: '#fff' };

/** 담당자 — 칩을 누르거나 직접 적기 */
// V161: 중국어(병음)·한글 입력기로 한 글자 적으면 그 이름이 바로 '예전에 쓴 이름' 칩이 되면서
//       칸이 비어 버리던 문제 — 적는 동안에는 칸 글자를 따로 들고 있고, 칩 목록도 적기 시작할 때 그대로 둔다.
//       입력기 조합 중(compositionstart~end)에는 저장하지 않는다.
export function OwnerPicker({ Z, value = '', onChange, owners = [], attr = 'owner' }) {
  const v = String(value || '');
  const [edit, setEdit] = useState(null);           // 적는 중: { text, base(그때의 칩 목록) }
  const composing = useRef(false);
  const list = edit ? edit.base : owners;
  const inList = list.includes(v.trim());
  const shown = edit ? edit.text : (inList ? '' : v);
  const typed = (t) => {
    setEdit(x => ({ base: x ? x.base : owners, text: t }));
    if (!composing.current) onChange(t);
  };
  return (
    <div data-owner-picker={attr} style={{ display: 'flex', flexWrap: 'wrap', gap: 6, alignItems: 'center' }}>
      <button type="button" data-owner-pick="" onClick={() => onChange('')} style={chipStyle(!v.trim(), 'blue')}>{Z('담당 없음', '不指定')}</button>
      {list.map(o => (
        <button key={o} type="button" data-owner-pick={o} onClick={() => onChange(o)} style={chipStyle(v.trim() === o && !(edit && edit.text.trim()), 'blue')}>👤 {o}</button>
      ))}
      <input data-owner-input value={shown} maxLength={OWNER_MAX}
        onFocus={() => setEdit({ text: inList ? '' : v, base: owners })}
        onBlur={(e) => { const t = e.currentTarget.value; composing.current = false; setEdit(null); if (t !== (inList ? '' : v) && t !== v) onChange(t); }}
        onCompositionStart={() => { composing.current = true; }}
        onCompositionEnd={(e) => { composing.current = false; const t = e.currentTarget.value; setEdit(x => (x ? { ...x, text: t } : x)); onChange(t); }}
        onChange={e => typed(e.target.value)}
        placeholder={owners.length ? Z('다른 이름', '其他名字') : Z('이름 적기', '写名字')}
        style={{ ...inputStyle, width: 120, padding: '6px 10px', borderRadius: 999, borderColor: v.trim() && !inList ? '#2563EB' : '#E4E4E7' }} />
    </div>
  );
}

/** 마감일 — 날짜만 */
export function DuePicker({ Z, value = '', onChange, today }) {
  const quick = [[0, '오늘', '今天'], [1, '내일', '明天'], [3, '3일 뒤', '3天后'], [7, '1주 뒤', '1周后']];
  return (
    <div data-due-picker style={{ display: 'flex', flexWrap: 'wrap', gap: 6, alignItems: 'center' }}>
      {quick.map(([n, ko, zh]) => {
        const k = addDaysKey(today, n);
        return <button key={n} type="button" data-due-quick={n} onClick={() => onChange(k)} style={chipStyle(value === k)}>{Z(ko, zh)}</button>;
      })}
      <input data-due-input type="date" value={value || ''} onChange={e => onChange(e.target.value)} style={{ ...inputStyle, padding: '5px 8px' }} />
      {value && (
        <button type="button" data-due-clear onClick={() => onChange('')}
          style={{ border: 'none', background: 'none', color: '#A1A1AA', fontSize: 13, fontWeight: 800, cursor: 'pointer', padding: '2px 4px' }}>✕ {Z('마감 없음', '不设')}</button>
      )}
    </div>
  );
}

/** 칸 이름 + 내용 한 줄 */
export function FieldRow({ label, children, top = false }) {
  return (
    <div style={{ display: 'flex', gap: 8, alignItems: top ? 'flex-start' : 'center' }}>
      <span style={{ fontSize: 12, fontWeight: 800, color: '#71717A', minWidth: 56, paddingTop: top ? 8 : 0 }}>{label}</span>
      <div style={{ flex: 1, minWidth: 0 }}>{children}</div>
    </div>
  );
}

export const textareaStyle = { ...inputStyle, width: '100%', resize: 'vertical', lineHeight: 1.55 };
export const textInputStyle = { ...inputStyle, width: '100%' };

/**
 * 위챗으로 보내기 — 휴대폰은 공유창(위챗 고르기), 컴퓨터는 복사 → 위챗 대화창에 붙여넣기
 * @returns 'shared' | 'copied' | 'fail' | null (공유창을 닫음)
 */
export async function shareText(text, isMobile) {
  if (isMobile && typeof navigator !== 'undefined' && navigator.share) {
    try { await navigator.share({ text }); return 'shared'; }
    catch (e) { if (e && e.name === 'AbortError') return null; /* 공유창 실패 → 복사로 */ }
  }
  try { await navigator.clipboard.writeText(text); return 'copied'; }
  catch (e) { /* 아래 옛 방식 */ }
  try {
    const ta = document.createElement('textarea');
    ta.value = text; ta.setAttribute('readonly', ''); ta.style.position = 'fixed'; ta.style.opacity = '0';
    document.body.appendChild(ta); ta.select();
    const ok = document.execCommand && document.execCommand('copy');
    ta.remove();
    return ok ? 'copied' : 'fail';
  } catch (e) { return 'fail'; }
}

export function ShareState({ Z, state }) {
  if (!state) return null;
  return (
    <div data-share-state={state} style={{ fontSize: 12.5, marginTop: 5, fontWeight: 700, color: state === 'fail' ? '#B91C1C' : '#15803D' }}>
      {state === 'copied' ? Z('✅ 복사됐어요 — 위챗 대화창에 붙여넣으세요', '✅ 已复制，请粘贴到微信对话框')
        : state === 'shared' ? Z('✅ 공유창으로 보냈어요', '✅ 已发送')
        : Z('⚠ 복사가 안 됐어요 — 위 글을 길게 눌러 복사하세요', '⚠ 复制失败，请长按上面的文字复制')}
    </div>
  );
}
